import React, { FC, useEffect, useState, Fragment } from 'react';
import { getShortLanguageCode } from 'utils/format';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tabs,
    Tab,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CONSTANTS } from 'config/constants';
import { TransportLocation } from 'models/transport';
import { Order } from 'models/order';
import { NShiftShipmentWaybill } from 'models/nshift-shipment-waybill';
import { Organization } from 'models/organization';
import TransportPoint from '../shared/transport-point.component';
import { downloadUrl } from 'utils/file';
import { currentUserVar } from 'apollo/reactive-variables/user';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TransportDate from '../shared/transport-date.component';
import { patientDisplayName } from 'utils/orders';

const { NSHIFT_DEVELOPER_ID } = CONSTANTS;

type WaybillsDetailsDialogProps = {
    waybills?: NShiftShipmentWaybill[];
    open: boolean;
    onClose: () => void;
};

const WaybillsDetailsDialog: FC<WaybillsDetailsDialogProps> = ({
    waybills,
    open,
    onClose,
}: WaybillsDetailsDialogProps) => {
    const { t: translate } = useTranslation(['transport', 'orders']);
    const [currentTabIndex, setCurrentTabIndex] = useState('');
    const [currentWaybill, setCurrentWaybill] = useState<NShiftShipmentWaybill>();
    const user = currentUserVar();

    useEffect(() => {
        if (waybills && waybills.length > 0) {
            setCurrentWaybill(waybills[0]);
            setCurrentTabIndex(waybills[0]._id);
        }
    }, [waybills]);

    const orderText = (order: Order) => {
        // DD-3620: see transport-details-dialog — a withheld patient must not
        // render as "undefined".
        const name = patientDisplayName(order.patient);
        if (!name) return order.orderId;
        return `${order.orderId} - ${name}, ${order.patient?.personalIdentityNumber ?? ''}`;
    };

    const navigateToOrder = (order: Order) => {
        const orderUrl = `/order-details/${order._id}/main-view`;
        window.open(orderUrl, '_blank');
    };

    const getTransportLocation = (org: Organization): TransportLocation => {
        return {
            organizationId: org.organizationNumber,
            name: org.name,
            streetAddress: org.contact?.address?.street || '',
            zipCode: org.contact?.address?.postalCode || '',
            city: org.contact?.address?.city || '',
            countryCode: org.contact.address.country,
            deliveryInstructions: '',
            phoneNumber: org.contact?.communication?.phone,
            emailAddress: org.contact?.communication?.email,
        };
    };

    const downloadWaybill = (waybill?: NShiftShipmentWaybill) => {
        if (waybill) {
            downloadUrl(`${waybill.prints[0].id}.pdf`, waybill.prints[0].fileUrl);
        }
    };

    const openTrackingLink = (waybill?: NShiftShipmentWaybill) => {
        if (waybill) {
            window.open(
                `https://www.unifaunonline.com/ext.uo.${getShortLanguageCode(user.localeFormat?.language)}.track?key=${NSHIFT_DEVELOPER_ID}&order=${waybill.orderNo}`,
            );
        }
    };

    const OrderList = ({ orders, manualPackages }: { orders: Order[]; manualPackages?: number }) => {
        return (
            <Fragment>
                <Box>
                    <Typography variant="subtitle2">{translate('orders:ORDERS')}</Typography>
                    <List disablePadding>
                        {orders?.map((order) => (
                            <ListItem key={order._id} disablePadding>
                                <ListItemButton onClick={() => navigateToOrder(order)}>
                                    <ListItemText sx={{ color: 'primary.main' }} primary={orderText(order)} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                        {manualPackages ? (
                            <ListItem disablePadding>
                                <ListItemButton>
                                    <ListItemText
                                        sx={{ color: 'primary.main' }}
                                        primary={`${manualPackages} ${translate('transport:PACKAGE_WITHOUT_ORDER')}`}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ) : undefined}
                    </List>
                </Box>
            </Fragment>
        );
    };

    const handleTabChange = (newTabIndex: string) => {
        setCurrentTabIndex(newTabIndex);
        setCurrentWaybill(waybills?.find((waybill) => waybill._id === newTabIndex));
    };

    return (
        <Fragment>
            {waybills ? (
                <Fragment>
                    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
                        <DialogTitle>{translate('transport:WAYBILLS')}</DialogTitle>
                        <DialogContent>
                            {currentWaybill ? (
                                <Box mb={2}>
                                    <Box>
                                        <Paper variant="outlined">
                                            <Box p={1}>
                                                {currentWaybill.createdAt && (
                                                    <Box mb={1}>
                                                        <TransportDate
                                                            label={translate(`transport:TRANSPORT_CREATED_DATE`)}
                                                            date={currentWaybill.createdAt}
                                                        />
                                                    </Box>
                                                )}
                                                <Box mb={1}>
                                                    <TransportPoint
                                                        location={getTransportLocation(
                                                            currentWaybill?.senderReference || ({} as Organization),
                                                        )}
                                                        isFrom={true}
                                                        date=""
                                                    />
                                                </Box>
                                                <Box>
                                                    <TransportPoint
                                                        location={getTransportLocation(
                                                            currentWaybill?.receiverReference || ({} as Organization),
                                                        )}
                                                        isFrom={false}
                                                        date=""
                                                    />
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Box>
                            ) : undefined}

                            {waybills && waybills.length > 1 ? (
                                <Tabs value={currentTabIndex} onChange={(_, value) => handleTabChange(value)}>
                                    {waybills.map((waybill, index) => (
                                        <Tab
                                            key={waybill._id}
                                            value={waybill._id}
                                            label={`${translate('transport:WAYBILL')} ${index + 1}`}
                                        />
                                    ))}
                                </Tabs>
                            ) : undefined}
                            <Paper variant="outlined" sx={{ padding: 2 }}>
                                {currentWaybill ? (
                                    <Fragment>
                                        <OrderList
                                            orders={currentWaybill.orders}
                                            manualPackages={currentWaybill.manualPackageCount}
                                        />
                                        {currentWaybill.internalMessage ? (
                                            <Box mt={2}>
                                                <Typography variant="subtitle2">
                                                    {translate('transport:INTERNAL_MESSAGE')}
                                                </Typography>
                                                <Box>{currentWaybill.internalMessage}</Box>
                                            </Box>
                                        ) : undefined}
                                    </Fragment>
                                ) : undefined}

                                <Box mt={2}>
                                    <Button
                                        sx={{ marginRight: 1 }}
                                        startIcon={<LocationOnIcon />}
                                        onClick={() => openTrackingLink(currentWaybill)}
                                    >
                                        {translate('transport:TRACK_SHIPMENT')}
                                    </Button>
                                    <Button onClick={() => downloadWaybill(currentWaybill)}>
                                        {translate('transport:PRINT_WAYBILL')}
                                    </Button>
                                </Box>
                            </Paper>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={onClose}>{translate('common:CLOSE')}</Button>
                        </DialogActions>
                    </Dialog>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default WaybillsDetailsDialog;
